import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, Form, Select, message, InputNumber, Image } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";

import { useCreateProductMutation } from "graphql/mutations/create-product.generated";
import { useGetProductCategoriesQuery } from "graphql/queries/get-product-categories.generated";
import { CreateProductInput, Product_Attachments_Insert_Input } from "graphql/generated/graphql";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";

import { ProductType } from "constants/field-values";
import routeNames from "constants/route-names";
import { pruneEmptyValues } from "utils/helpers";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { getFileExtension, uploadToStorage } from "utils/files-management";

const { TextArea } = Input;

const ProductAdd: React.FC = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { updateSelectedAttachments } = useSelectedAttachments();
	const [name, setName] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [productNotes, setProductNotes] = useState<string>("");
	const [sku, setSku] = useState<string>("");
	const [type, setType] = useState<ProductType | string>("");
	const [model, setModel] = useState<string>("");
	const [make, setMake] = useState<string>("");
	const [productTypeOptions, setProductTypeOptions] = useState<{ value: string; label: string }[]>([]);
	const [retailPrice, setRetailPrice] = useState<number | null>(null);
	const [extendedPrice, setExtendedPrice] = useState<number | null>(null);
	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<Product_Attachments_Insert_Input | any>>([]);
	const [attachments, setAttachments] = useState<Product_Attachments_Insert_Input[]>([]);
	const [productInputCompleted, setProductInputCompleted] = useState(false);
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);
	const [productPhotoThumbUrl, setProductPhotoThumbUrl] = useState<string>("");
	const [productPhotoFile, setProductPhotoFile] = useState<RcFile | null>(null);
	const [productPhotoUrl, setProductPhotoUrl] = useState<string>("");

	const handleOnCompleteCreateProductMutation = useCallback((data: any) => {
		// console.log("OnComplete Create Product Mutation data: ", data);
		setUploading(false);
		navigate(routeNames.ADMIN_DEVICES);
	}, []);

	const handleOnErrorCreateProductMutation = useCallback((error: ApolloError) => {
		console.log("useCreateProductMutation Error: ", error);
		setUploading(false);
		message.error("Failed to save the Product.");
		navigate(routeNames.ADMIN_DEVICES);
	}, []);

	const [createProductMutation, { data, loading, error }] = useCreateProductMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorCreateProductMutation,
		onCompleted: handleOnCompleteCreateProductMutation
	});

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file } = attachmentFile;

			const newList = attachmentsFiles.filter(element => element.uid !== uid);

			setAttachmentsFiles(newList);
		},
		[attachmentsFiles]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file, name } = attachmentFile;

			updateSelectedAttachments(attachmentFile);

			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { type, uid } = attachmentFile;

		setAttachmentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);

			return filteredList.concat([attachmentFile]);
		});
	}, []);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setProductPhotoThumbUrl(url);
		setProductPhotoFile(file);
	}, []);

	const handleOnRemovePhoto = useCallback(async () => {
		// Clear local state
		setProductPhotoThumbUrl("");
		setProductPhotoFile(null);
	}, []);

	const uploadAttachments = useCallback(async () => {
		if (attachmentsFiles.length > 0) {
			try {
				const promises: Promise<Product_Attachments_Insert_Input | undefined>[] = [];

				attachmentsFiles.forEach(attachment => {
					const { type, description, file, name } = attachment;

					const fileMediaType = file.type.split("/")[0];
					const uniqueID = uuidv4();

					let bucketName = "products-attachments/";
					let attachmentName = "";
					const fileExtension = getFileExtension(file.name);
					let fileName = name ? `${name}.${fileExtension}` : file.name;

					const newAttachment: Product_Attachments_Insert_Input = {
						photo_link: null,
						document_link: null,
						video_link: null,
						audio_link: null,
						type,
						description,
						name,
						mime_type: file.type,
					};

					switch (fileMediaType) {
						case "image":
							bucketName = bucketName + "photos";
							attachmentName = `attachment-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "video":
							bucketName = bucketName + "videos";
							attachmentName = `attachment-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "audio":
							bucketName = bucketName + "audios";
							attachmentName = `attachment-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						default:
							bucketName = bucketName + "documents";
							attachmentName = `attachment-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
					}

					const uploadTask = async () => {
						try {
							const fileObj = file?.originFileObj;
							const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`);

							switch (fileMediaType) {
								case "image":
									newAttachment.photo_link = publicUrl;
									break;
								case "video":
									newAttachment.video_link = publicUrl;
									break;
								case "audio":
									newAttachment.audio_link = publicUrl;
									break;
								default:
									newAttachment.document_link = publicUrl;
									break;
							}

							return newAttachment;
						} catch (error) {
							console.log("Error when trying to upload the attachment file to firestore. Error: ", error);
						}
					};

					promises.push(uploadTask());
				});

				try {
					const promisesResults = await Promise.all(promises);

					const filteredPromisesResults = pruneEmptyValues(promisesResults) as Product_Attachments_Insert_Input[];

					setAttachments(filteredPromisesResults);
				} catch (error) {
					console.log("Error trying to upload attachments: ", error);
				}
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}

		return;
	}, [attachmentsFiles]);

	const uploadProductImg = useCallback(async () => {
		if (productPhotoFile) {
			try {
				const uniqueID = uuidv4();

				const bucketName = "product-photos/";

				const fileName = `product-photo${name ? `-${name}` : ""}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${
					productPhotoFile.name
				}`;

				const { publicUrl } = await uploadToStorage(productPhotoFile, () => {}, `${bucketName}/${fileName}`);

				setProductPhotoUrl(publicUrl);
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}
	}, [productPhotoFile, name]);

	const handleOnSubmit = useCallback(async () => {
		if (!type) {
			message.error("Please select a device type.");
			return;
		}

		setUploading(true);

		if (productPhotoFile) {
			await uploadProductImg();
		}

		if (attachmentsFiles.length) {
			await uploadAttachments();
		}

		setProductInputCompleted(true);
	}, [attachmentsFiles, productPhotoFile, type]);

	const uploadProduct = useCallback(async () => {
		let productInput: CreateProductInput = {
			name,
			description,
			type,
			model,
			make,
			sku: sku ? sku : null,
			retail_price: retailPrice,
			extended_price: extendedPrice,
			product_notes: productNotes,
			workspace_id: loggedUserValue.workspace_id,
			organization_id: loggedUserValue.organization_id
		};

		if (productPhotoUrl) {
			productInput.photo_link = productPhotoUrl;
		}

		if (attachments && attachments.length > 0) {
			productInput.attachments = attachments;
		}

		createProductMutation({
			variables: {
				input: productInput
			}
		});
	}, [
		name,
		description,
		sku,
		type,
		model,
		make,
		retailPrice,
		extendedPrice,
		productNotes,
		attachmentsFiles,
		attachments,
		loggedUserValue,
		productPhotoUrl
	]);

	const { loading: loadingProductTypes } = useGetProductCategoriesQuery({
		fetchPolicy: "network-only",
		onCompleted: data => {
			setProductTypeOptions(data.product_categories.map(pc => ({ value: pc.name, label: pc.label })));
		}
	});

	useEffect(() => {
		if (productInputCompleted) {
			uploadProduct();
		}
	}, [productInputCompleted]);

	const isLoading = uploading || loading || loadingProductTypes;

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"New Device"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Device Details"}</span>
				<Form.Item
					className="product-add-form-item"
					rules={[{ required: true, message: "Please input the device name." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="name"
						placeholder="Name"
						className="add-product-input-pair"
						value={name}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<div className="two-inputs-container">
					<Form.Item
						className="product-add-form-item-pair"
						rules={[
							{
								required: true,
								message: "Please input the make of the Device."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Input
							name="make"
							placeholder="Make"
							className="add-product-input-pair"
							value={make}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setMake(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item
						className="product-add-form-item-pair"
						rules={[
							{
								required: true,
								message: "Please input the model of the Device."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Input
							name="model"
							placeholder="Model"
							className="add-product-input-pair"
							value={model}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setModel(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>
				</div>

				<div className="two-inputs-container">
					<Form.Item
						className="product-add-form-item-pair"
						rules={[
							{
								required: true,
								message: "Please input the SKU of the Device."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Input
							name="sku"
							placeholder="SKU"
							className="add-product-input-pair"
							value={sku}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setSku(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item
						className="product-add-form-item-pair"
						// required and rules doesn't seem to work
						rules={[{ required: true, message: "Please select a Device Type." }]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={type}
							className="add-product-input-select"
							onChange={value => setType(value)}
							options={productTypeOptions}
							placeholder="Device Type"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				</div>
				<Form.Item className="product-add-form-item-pair">
					<InputNumber
						name="retailPrice"
						placeholder="Retail Price"
						className="add-product-input-pair"
						value={retailPrice}
						disabled={isLoading}
						onChange={value => {
							setRetailPrice(value);
						}}
					/>
				</Form.Item>
				<Form.Item className="product-add-form-item">
					<TextArea
						name="description"
						placeholder="Description"
						style={{ height: "148px" }}
						value={description}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
							setDescription(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item className="product-add-form-item">
					<TextArea
						name="product-notes"
						placeholder="Device Notes"
						style={{ height: "137px" }}
						value={productNotes}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
							setProductNotes(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Button className="upload-product-button" onClick={() => setAddProductPhotoModalOpen(true)}>
					Upload Device Image
				</Button>

				{productPhotoThumbUrl ? (
					<div className="organization-button-container">
						<Image width={170} src={productPhotoThumbUrl} />{" "}
						<img
							alt={`remove-organization-photo-icon`}
							src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
							className="organization-photo-action-icon"
							onClick={() => {
								if (!isLoading) {
									handleOnRemovePhoto();
								}
							}}
						/>
					</div>
				) : null}

				<UploadPhotoModal
					open={addProductPhotoModalOpen}
					onCancel={() => setAddProductPhotoModalOpen(false)}
					uploadMethod={handleOnConfirmPhoto}
					disableCrop
				/>
			</Form>

			<div className="add-product-form">
				<span className="add-product-form-title">{"Device Resources"}</span>

				<div className="add-product-documents">
					<Button
						className="upload-product-button"
						onClick={() => setAddAttachmentModalOpen(true)}
						disabled={isLoading}
					>
						Add Resources
					</Button>
				</div>

				{attachmentsFiles && attachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{attachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveAttachment(attachmentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditAttachment(attachmentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}
			</div>

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit}>
					Save
				</Button>
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
			/>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default ProductAdd;
